import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

function formatAmount(num) {
    if (num === null || num === undefined || num === '' || Number.isNaN(num)) return '0,00';
    const n = parseFloat(num);
    const abs = Math.abs(n).toFixed(2);
    const parts = abs.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (n < 0 ? '-' : '') + intPart + ',' + parts[1];
}

function numberToFrenchWords(amount) {
    if (!amount || Number.isNaN(amount)) { return 'Zéro euro.'; }

    const ones = [
        '', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
        'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
        'dix-sept', 'dix-huit', 'dix-neuf'
    ];
    const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante',
        'soixante', 'quatre-vingt', 'quatre-vingt'];

    function below1000(n) {
        if (n === 0) { return ''; }
        if (n < 20) { return ones[n]; }
        const t = Math.floor(n / 10);
        const o = n % 10;
        if (n < 100) {
            if (t === 7) { return 'soixante-' + ones[10 + o]; }
            if (t === 9) { return o === 0 ? 'quatre-vingt-dix' : 'quatre-vingt-' + ones[10 + o]; }
            if (o === 0) { return t === 8 ? 'quatre-vingts' : tens[t]; }
            if (o === 1 && t !== 8) { return tens[t] + ' et un'; }
            return tens[t] + '-' + ones[o];
        }
        const h = Math.floor(n / 100);
        const rem = n % 100;
        const centStr = h === 1 ? 'cent' : ones[h] + ' cent' + (rem === 0 && h > 1 ? 's' : '');
        return centStr + (rem > 0 ? ' ' + below1000(rem) : '');
    }

    function convertInt(n) {
        if (n === 0) { return 'zéro'; }
        let r = '';
        const billions = Math.floor(n / 1000000000);
        const millions = Math.floor((n % 1000000000) / 1000000);
        const thousands = Math.floor((n % 1000000) / 1000);
        const remainder = n % 1000;
        if (billions > 0) { r += below1000(billions) + ' milliard' + (billions > 1 ? 's' : '') + ' '; }
        if (millions > 0) { r += below1000(millions) + ' million' + (millions > 1 ? 's' : '') + ' '; }
        if (thousands > 0) { r += (thousands === 1 ? '' : below1000(thousands) + ' ') + 'mille '; }
        if (remainder > 0) { r += below1000(remainder); }
        return r.trim();
    }

    const intPart = Math.floor(Math.abs(amount));
    const decPart = Math.round((Math.abs(amount) - intPart) * 100);

    let words = convertInt(intPart);
    words = words.charAt(0).toUpperCase() + words.slice(1);
    words += ' euro' + (intPart > 1 ? 's' : '');
    if (decPart > 0) {
        words += ' et ' + convertInt(decPart) + ' centime' + (decPart > 1 ? 's' : '');
    }
    return words + '.';
}

function ibanToRib(iban) {
    if (!iban) { return null; }
    const raw = iban.replace(/\s/g, '').toUpperCase();
    if (!raw.startsWith('FR') || raw.length < 27) { return null; }
    const rib = raw.slice(4);
    const bank = rib.slice(0, 5);
    const branch = rib.slice(5, 10);
    const account = rib.slice(10, 21);
    const key = rib.slice(21, 23);
    return `${bank} ${branch} ${account} - ${key}`;
}

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 220,
        paddingHorizontal: 45,
        color: '#1a1a1a',
    },
    header: {
        flexDirection: 'row',
        marginBottom: 18,
        alignItems: 'flex-start',
    },
    logoContainer: {
        width: 130,
        marginRight: 10,
    },
    logo: {
        maxWidth: 120,
        maxHeight: 120,
        objectFit: 'contain',
    },
    headerRight: {
        flex: 1,
        alignItems: 'flex-end',
        marginTop: 10,
    },
    clientName: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        textAlign: 'left',
    },
    clientLine: {
        fontSize: 9,
        textAlign: 'left',
        marginTop: 5,
    },
    dateText: {
        fontSize: 10,
        marginTop: 20,
        textAlign: 'right',
    },
    factureNum: {
        fontSize: 9,
        color: '#CC0000',
        marginBottom: 14,
    },
    titleBox: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        backgroundColor: '#f7f7f7',
        paddingVertical: 10,
        paddingHorizontal: 16,
        alignItems: 'center',
        marginBottom: 12,
    },
    titleBoxText: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 14,
        color: '#222222',
    },
    table: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        marginBottom: 14,
    },
    row: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#e2e2e2',
    },
    rowHeader: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#aaaaaa',
        backgroundColor: '#f1f1f1',
    },
    rowTotal: {
        flexDirection: 'row',
        backgroundColor: '#eeeeee',
    },
    colRef: {
        width: 90,
        padding: 6,
        fontSize: 9,
        borderRightWidth: 1,
        borderColor: '#d6d6d6',
    },
    colOperation: {
        flex: 1,
        padding: 6,
        fontSize: 9,
        borderRightWidth: 1,
        borderColor: '#d6d6d6',
    },
    colAmount: {
        width: 140,
        padding: 6,
        fontSize: 9,
        textAlign: 'right',
    },
    montantRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 14,
    },
    selectedYearText: {
        fontSize: 10,
        marginBottom: 6,
    },
    montantLabel: {
        fontSize: 10,
    },
    montantValue: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        color: '#CC0000',
    },
    enLettreRow: {
        flexDirection: 'row',
        marginBottom: 20,
        flexWrap: 'wrap',
    },
    enLettreLabel: {
        fontSize: 10,
    },
    enLettreValue: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 10,
        textDecoration: 'underline',
        flex: 1,
    },
    paymentBox: {
        borderWidth: 1,
        borderColor: '#aaaaaa',
        padding: 10,
        marginBottom: 20,
        fontSize: 9,
        lineHeight: 1.6,
    },
    paymentHighlight: {
        color: '#CC0000',
    },
    signatureRow: {
        flexDirection: 'row',
        marginTop: 6,
    },
    signatureRight: {
        flex: 1,
        alignItems: 'center',
    },
    tamponImage: {
        maxHeight: 80,
        objectFit: 'contain',
    },
    bold: {
        fontFamily: 'Helvetica-Bold',
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 45,
        right: 45,
        borderTopWidth: 1,
        borderColor: '#cccccc',
        paddingTop: 8,
        alignItems: 'center',
    },
    footerPaymentBox: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#aaaaaa',
        padding: 8,
        marginBottom: 8,
        fontSize: 8,
        lineHeight: 1.5,
    },
    footerSignatureRow: {
        width: '100%',
        flexDirection: 'row',
        marginBottom: 6,
    },
    footerText: {
        fontSize: 7,
        color: '#666666',
        textAlign: 'center',
    },
});

const FacturationSelectionPDF = ({ mode, annee, date, organisme, rows, totalAmount, client, numFacture, dateEcheance }) => {
    const title = mode === 'avis' ? "AVIS D'ÉCHÉANCE" : 'FACTURE';
    const amountLabel = mode === 'avis' ? 'Montant avis' : 'Montant TTC';
    const rowsPerPage = 10;

    const buildClientLines = (c) => {
        if (!c) {
            return [];
        }
        const lines = [];
        if (c.nom) {
            lines.push(c.nom);
        }
        if (c.nom_complement) {
            lines.push(c.nom_complement);
        }
        const street = [c.num_voie, c.num_suffixe, c.type_voie, c.nom_voie].filter(Boolean).join(' ');
        if (street) {
            lines.push(street);
        }
        if (c.complement_adresse) {
            lines.push(c.complement_adresse);
        }
        const city = [c.code_postale, c.ville ? c.ville.toUpperCase() : ''].filter(Boolean).join(' ');
        if (city) {
            lines.push(city);
        }
        return lines;
    };

    const clientLines = buildClientLines(client);

    const buildFooter = () => {
        const parts = [];
        if (organisme.nom_organisme) { parts.push(organisme.nom_organisme); }
        const street = [organisme.num_voie, organisme.num_suffixe, organisme.type_voie, organisme.nom_voie].filter(Boolean).join(' ');
        if (street) { parts.push(street); }
        if (organisme.complement_adresse) { parts.push(organisme.complement_adresse); }
        const city = [organisme.code_postale, organisme.ville].filter(Boolean).join(' ');
        if (city) { parts.push(city); }
        if (organisme.telephone) { parts.push(`Tél. : ${organisme.telephone}`); }
        if (organisme.email_contact) { parts.push(organisme.email_contact); }
        return parts.join(' - ');
    };

    const safeRows = Array.isArray(rows) ? rows : [];
    const paginatedRows = [];
    for (let i = 0; i < safeRows.length; i += rowsPerPage) {
        paginatedRows.push(safeRows.slice(i, i + rowsPerPage));
    }
    if (paginatedRows.length === 0) {
        paginatedRows.push([]);
    }

    return (
        <Document>
            {paginatedRows.map((rowsPage, pageIndex) => {
                const isLastPage = pageIndex === paginatedRows.length - 1;
                return (
                    <Page key={pageIndex} size="A4" style={styles.page}>
                        <View style={styles.header}>
                            <View style={styles.logoContainer}>
                                {organisme.logo ? (
                                    <Image style={styles.logo} src={organisme.logo} />
                                ) : null}
                            </View>
                            <View style={styles.headerRight}>
                                {clientLines.map((line, i) => (
                                    <Text key={i} style={i === 0 ? styles.clientName : styles.clientLine}>
                                        {line}
                                    </Text>
                                ))}
                                <Text style={styles.dateText}>{date}</Text>
                            </View>
                        </View>

                        <Text style={styles.factureNum}>{title.charAt(0) + title.slice(1).toLowerCase()} n° {numFacture}</Text>

                        <View style={styles.titleBox}>
                            <Text style={styles.titleBoxText}>{title}</Text>
                        </View>

                        <View style={styles.table}>
                            <View style={styles.rowHeader}>
                                <Text style={[styles.colRef, styles.bold]}>Référence</Text>
                                <Text style={[styles.colOperation, styles.bold]}>Operation</Text>
                                <Text style={[styles.colAmount, styles.bold]}>{amountLabel}</Text>
                            </View>

                            {rowsPage.map((row, idx) => (
                                <View key={idx} style={styles.row}>
                                    <Text style={styles.colRef}>{row.reference || ''}</Text>
                                    <Text style={styles.colOperation}>{row.operation || ''}</Text>
                                    <Text style={styles.colAmount}>{formatAmount(row.amount)} euros</Text>
                                </View>
                            ))}

                            {isLastPage ? (
                                <View style={styles.rowTotal}>
                                    <Text style={[styles.colRef, styles.bold]}>Total</Text>
                                    <Text style={[styles.colOperation, styles.bold]}></Text>
                                    <Text style={[styles.colAmount, styles.bold]}>{formatAmount(totalAmount)} euros</Text>
                                </View>
                            ) : null}
                        </View>

                        {isLastPage ? (
                            <>
                                {annee ? (
                                    <Text style={styles.selectedYearText}>{`Année : ${annee}`}</Text>
                                ) : null}

                                <View style={styles.montantRow}>
                                    <Text style={styles.montantLabel}>Montant à régler : </Text>
                                    <Text style={styles.montantValue}>{formatAmount(totalAmount)} euros</Text>
                                </View>

                                <View style={styles.enLettreRow}>
                                    <Text style={styles.enLettreLabel}>En lettre : </Text>
                                    <Text style={styles.enLettreValue}>{numberToFrenchWords(totalAmount)}</Text>
                                </View>

                            </>
                        ) : null}

                        <View style={styles.footer} fixed>
                            <View style={styles.footerPaymentBox}>
                                <Text>Valeur en votre aimable règlement par virement</Text>
                                {ibanToRib(organisme.iban) ? (
                                    <Text>
                                        {'RIB : '}
                                        <Text style={styles.paymentHighlight}>{ibanToRib(organisme.iban)}</Text>
                                    </Text>
                                ) : null}
                                {organisme.iban ? (
                                    <Text>
                                        {'IBAN : '}
                                        <Text style={styles.paymentHighlight}>{organisme.iban}</Text>
                                    </Text>
                                ) : null}
                                {organisme.bic ? <Text>{`BIC : ${organisme.bic}`}</Text> : null}
                                <Text>
                                    {'Référence à rappeler '}
                                    <Text style={styles.paymentHighlight}>{numFacture}</Text>
                                </Text>
                                {dateEcheance ? (
                                    <Text style={{marginTop: 4}}>
                                        {`À compter du ${dateEcheance} des pénalités de retard de paiement seront appliquées (article 4.2.4 de notre règlement d'intervention).`}
                                    </Text>
                                ) : null}
                            </View>
                            <View style={styles.footerSignatureRow}>
                                <View style={{ flex: 1 }} />
                                <View style={styles.signatureRight}>
                                    {organisme.tampon_signature ? (
                                        <Image style={styles.tamponImage} src={organisme.tampon_signature} />
                                    ) : null}
                                </View>
                            </View>
                            <Text style={styles.footerText}>{buildFooter()}</Text>
                            {organisme.pied_page ? (
                                <Text style={styles.footerText}>{organisme.pied_page}</Text>
                            ) : null}
                        </View>
                    </Page>
                );
            })}
        </Document>
    );
};

export default FacturationSelectionPDF;
